import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
import ProductCard from '../components/ProductCard';
import { productService, userService, albumService } from '../services/api';
import { ownedCountFromBase64, VISIBLE_TOTAL } from '../album/albumData';
import { useAuth } from '../hooks/useAuth';
import { formatDate as formatLocaleDate } from '../utils/i18n';
import { buildGoMessagesPath } from '../utils/redirect';
import { getImageUrl } from '../utils/imageUtils';
import { useCommunityModeration } from '../hooks/useCommunityModeration';
import UserSchedulesSection from '../components/sedes/UserSchedulesSection';

function UserProfileNew({ avatarShape = 'rounded-full' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const theme = getThemeVars(ACTIVE_THEME);

  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [productsCursor, setProductsCursor] = useState(null);
  const [productsHasMore, setProductsHasMore] = useState(false);
  const [productsLoadingMore, setProductsLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [kickConfirm, setKickConfirm] = useState(null); // { communityId, communityName }
  const [banModal, setBanModal] = useState(null);        // { communityId, communityName }
  const menuRef = useRef(null);
  const { kickFromCommunity, banFromCommunity, toast: moderationToast } = useCommunityModeration();

  const targetId = parseInt(id);
  const isOwnProfileCheck = currentUser?.id === targetId;

  const { data: moderationContext = [] } = useQuery({
    queryKey: ['moderation-context', id],
    queryFn: async () => {
      const res = await userService.getModerationContext(id);
      return res.data || [];
    },
    enabled: !isOwnProfileCheck && !!currentUser,
  });

  // Album del user (para mostrar la card dorada). Falla silencioso si no
  // comparten comunidad o si el user no tiene álbum.
  const { data: userAlbum = null } = useQuery({
    queryKey: ['user-album-summary', id],
    queryFn: async () => {
      const res = await albumService.getUserAlbum(id);
      return res.data || null;
    },
    enabled: !!currentUser && !!id,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const albumOwnedCount = userAlbum?.data ? ownedCountFromBase64(userAlbum.data) : 0;
  const albumHasContent = albumOwnedCount > 0;

  useEffect(() => { loadUserProfile(); }, [id]);

  const loadUserProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const userResponse = await userService.getUserById(id);
      const userData = userResponse.data;
      setUser(userData);

      try {
        const productsResponse = await productService.getProductsByCreatorCursor({ creatorId: id, pageSize: 16 });
        const productsData = productsResponse.data.data || productsResponse.data;
        setProducts(productsData?.items || []);
        setProductCount(userData?.productCount ?? productsData?.items?.length ?? 0);
        setProductsCursor(productsData?.nextCursor || null);
        setProductsHasMore(productsData?.hasMore || false);

      } catch {
        setProducts([]);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError(t('Usuario no encontrado o no compartes ninguna comunidad con este usuario'));
      } else {
        setError(t('Error al cargar el perfil del usuario'));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreProducts = async () => {
    if (!productsCursor || productsLoadingMore) return;
    setProductsLoadingMore(true);
    try {
      const response = await productService.getProductsByCreatorCursor({ creatorId: id, pageSize: 16, cursor: productsCursor });
      const data = response.data.data || response.data;
      setProducts((prev) => [...prev, ...(data?.items || [])]);
      setProductsCursor(data?.nextCursor || null);
      setProductsHasMore(data?.hasMore || false);
    } catch { /* ignore */ }
    finally { setProductsLoadingMore(false); }
  };

  const handleContactUser = () => {
    // Desde el perfil: chat directo user-to-user sin productId. Antes elegía
    // arbitrariamente "el primer producto del user" y lo pre-adjuntaba, lo
    // cual no tenía sentido — el user no entró desde un producto.
    navigate(buildGoMessagesPath({ userId: id }));
  };

  // formatLocaleDate already handles the LocalDateTime int-array format and uses
  // the active i18n locale, so we just delegate.
  const formatDate = (dateValue) => {
    if (!dateValue) return t('Fecha no disponible');
    return formatLocaleDate(dateValue, { options: { year: 'numeric', month: 'long' } });
  };

  const isOwnProfile = isOwnProfileCheck;
  const averageRating = user?.averageRating || 0;
  const totalReviews = user?.reviewCount || 0;

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-6xl mx-auto">
      {/* Loading */}
      {loading && (
        <div className="space-y-6">
          <div className="bg-surface-container rounded-2xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-center">
              <div className={`skeleton w-20 h-20 ${avatarShape}`} />
              <div className="flex-1 space-y-3">
                <div className="skeleton h-7 w-48 rounded" />
                <div className="skeleton h-4 w-32 rounded" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 skeleton h-64 rounded-2xl" />
            <div className="skeleton h-48 rounded-2xl" />
          </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-4 rounded-xl bg-error-container text-on-error-container">
            <span className="material-symbols-outlined">error</span>
            {error}
          </div>
          <button onClick={() => navigate(-1)} className="px-6 py-2.5 rounded-xl border-2 border-outline-variant/30 text-on-surface font-medium hover:bg-surface-container-low transition-colors">
            {t('Volver')}
          </button>
        </div>
      )}

      {/* Content */}
      {!loading && !error && user && (
        <div className="space-y-6">

          {/* ── User Header Card ────────────────────────────────────── */}
          <header className="bg-surface-container rounded-2xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-center">
              {/* Avatar */}
              <div className="shrink-0">
                {user.profilePictureUrl || user.profilePictureId ? (
                  <img
                    src={getImageUrl(user.profilePictureId) || user.profilePictureUrl}
                    alt={user.name}
                    className={`w-20 h-20 ${avatarShape} object-cover border-2 border-outline-variant/10 shadow-sm`}
                  />
                ) : (
                  <div className={`w-20 h-20 ${avatarShape} bg-primary-container border-2 border-outline-variant/10 shadow-sm flex items-center justify-center`}>
                    <span className="text-3xl font-black text-on-primary-container">{user.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                )}
              </div>

              {/* Name + metadata */}
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <h1 className="text-2xl sm:text-3xl font-black text-on-surface tracking-tight">{user.name}</h1>
                  {user.verified && (
                    <span className="material-symbols-outlined text-primary text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
                  )}
                </div>
                <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3 mt-2">
                  <span className="flex items-center gap-1.5 text-xs text-on-surface-variant">
                    <span className="material-symbols-outlined text-[14px] text-primary">storefront</span>
                    <span className="font-bold text-on-surface">{productCount || products.length}</span> {t('productos')}
                  </span>
                  <span className="w-px h-3.5 bg-outline-variant/30 hidden sm:block" />
                  <span className="flex items-center gap-1.5 text-xs text-on-surface-variant">
                    <span className="material-symbols-outlined text-[14px]">calendar_today</span>
                    {t('Miembro desde')} {formatDate(user.createdAt)}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                {isOwnProfile && (
                  <button onClick={() => navigate('/profile')} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-outline-variant/30 text-on-surface font-medium text-sm hover:bg-surface-container-high/50 transition-colors">
                    <span className="material-symbols-outlined text-[16px]">edit</span>
                    {t('Editar Perfil')}
                  </button>
                )}
                {!isOwnProfile && (
                  <button
                    onClick={handleContactUser}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-on-primary font-bold text-sm hover:opacity-90 transition-opacity"
                  >
                    <span className="material-symbols-outlined text-[16px]">forum</span>
                    {t('Mandar mensaje')}
                  </button>
                )}
                {/* Admin moderation menu */}
                {!isOwnProfile && moderationContext.length > 0 && (
                  <div className="relative" ref={menuRef}>
                    <button
                      onClick={() => setMenuOpen((v) => !v)}
                      className="w-9 h-9 rounded-xl border border-outline-variant/30 flex items-center justify-center hover:bg-surface-container transition-colors"
                    >
                      <span className="material-symbols-outlined text-[20px]">more_vert</span>
                    </button>
                    {menuOpen && (
                      <div className="absolute right-0 top-full mt-1 z-20 bg-surface-container-lowest rounded-xl shadow-xl border border-outline-variant/10 py-1 min-w-[220px]">
                        {moderationContext.map((comm) => (
                          <React.Fragment key={comm.id}>
                            <div className="px-3 py-1.5 text-[10px] text-on-surface-variant uppercase font-bold tracking-widest">{comm.name}</div>
                            <button
                              onClick={() => { setMenuOpen(false); setKickConfirm({ communityId: comm.id, communityName: comm.name }); }}
                              className="w-full text-left px-4 py-2 text-sm text-on-surface hover:bg-surface-container transition-colors flex items-center gap-2"
                            >
                              <span className="material-symbols-outlined text-[16px]">person_remove</span>
                              {t('Kick from community')}
                            </button>
                            <button
                              onClick={() => { setMenuOpen(false); setBanModal({ communityId: comm.id, communityName: comm.name }); }}
                              className="w-full text-left px-4 py-2 text-sm text-error hover:bg-error/5 transition-colors flex items-center gap-2"
                            >
                              <span className="material-symbols-outlined text-[16px]">block</span>
                              {t('Ban from community')}
                            </button>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* ── Card Grid ──────────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── Products Card (2 cols) ──────────────────────────── */}
            <div className="lg:col-span-2 bg-surface-container rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between p-5 pb-0">
                <h2 className="text-lg font-bold tracking-tight text-on-surface flex items-center gap-2">
                  <span className="material-symbols-outlined text-[20px] text-primary">storefront</span>
                  {t('Productos')} ({productCount || products.length})
                </h2>
              </div>

              {products.length === 0 ? (
                <div className="p-8 text-center">
                  <span className="material-symbols-outlined text-[48px] text-on-surface-variant/20">inventory_2</span>
                  <p className="text-sm text-on-surface-variant mt-3">
                    {isOwnProfile ? t('No tienes productos publicados') : t('Este usuario no tiene productos publicados')}
                  </p>
                  {isOwnProfile && (
                    <button onClick={() => navigate('/create-product')} className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-primary text-on-primary font-medium text-sm hover:opacity-90 transition-opacity">
                      <span className="material-symbols-outlined text-[16px]">add</span>
                      {t('Publicar')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="p-5 pt-4">
                  <div className="grid grid-cols-2 mobile-md:grid-cols-3 gap-4">
                    {products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onClick={() => navigate(`/products/${product.id}`)}
                        hideSeller
                        hideCommunity
                      />
                    ))}
                  </div>
                  {productsHasMore && (
                    <div className="mt-4 text-center">
                      <button
                        onClick={loadMoreProducts}
                        disabled={productsLoadingMore}
                        className="px-6 py-2.5 rounded-xl border border-outline-variant/30 text-on-surface font-medium text-sm hover:bg-surface-container-high/50 transition-colors disabled:opacity-50"
                      >
                        {productsLoadingMore ? t('Cargando...') : t('Cargar más')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Stats & Info Card ───────────────────────────────── */}
            <div className="space-y-6">

              {/* Schedules — comunidades compartidas con horarios del target */}
              {targetId && (
                <UserSchedulesSection
                  userId={targetId}
                  userName={user?.name}
                  isOwn={isOwnProfile}
                />
              )}

              {/* Stats */}
              <div className="bg-surface-container rounded-2xl p-5">
                <h2 className="text-lg font-bold tracking-tight text-on-surface flex items-center gap-2 mb-4">
                  <span className="material-symbols-outlined text-[20px] text-primary">bar_chart</span>
                  {t('Estadísticas')}
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary-container/50 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-[18px] text-on-primary-container">storefront</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-wider">{t('Productos')}</p>
                      <p className="text-sm font-medium text-on-surface">{productCount || products.length}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-surface-container-highest/50 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-[18px] text-on-surface-variant">calendar_today</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-wider">{t('Miembro desde')}</p>
                      <p className="text-sm font-medium text-on-surface">{formatDate(user.createdAt)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Album card (dorada, sólo si el user tiene álbum) ── */}
              {albumHasContent && (
                <button
                  type="button"
                  onClick={() => navigate(`/album?userId=${id}`)}
                  className="w-full text-left rounded-2xl p-5 relative overflow-hidden cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.99]"
                  style={{
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
                    boxShadow: '0 10px 30px -10px rgba(217, 119, 6, 0.5)',
                  }}
                >
                  {/* Glow decorativo */}
                  <div
                    aria-hidden
                    className="absolute -top-8 -right-8 w-32 h-32 rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, transparent 70%)' }}
                  />
                  <div className="relative flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-white/25 flex items-center justify-center shrink-0 backdrop-blur-sm">
                      <span
                        className="material-symbols-outlined text-[22px] text-white"
                        style={{ fontVariationSettings: "'FILL' 1" }}
                      >
                        emoji_events
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-white/80 uppercase font-bold tracking-widest">{t('Álbum del Mundial')}</p>
                      <p className="text-base font-black text-white truncate">{t('Ver álbum de {{name}}', { name: user.name?.split(' ')[0] || t('este usuario') })}</p>
                    </div>
                    <span className="material-symbols-outlined text-white/80">arrow_forward</span>
                  </div>
                  <div className="relative">
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-2xl font-black text-white tabular-nums">
                        {albumOwnedCount} <span className="text-sm font-bold text-white/70">/ {VISIBLE_TOTAL}</span>
                      </span>
                      <span className="text-xs font-bold text-white/90 tabular-nums">
                        {Math.round((albumOwnedCount / VISIBLE_TOTAL) * 100)}%
                      </span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-black/20 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-white"
                        style={{ width: `${Math.min(100, (albumOwnedCount / VISIBLE_TOTAL) * 100)}%` }}
                      />
                    </div>
                  </div>
                </button>
              )}

            </div>
          </div>
        </div>
      )}
        </div>
      </main>

      {/* Kick confirm modal */}
      {kickConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setKickConfirm(null)} />
          <div className="relative bg-surface-container-lowest rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="text-base font-bold text-on-surface mb-1">{t('Are you sure you want to remove {{name}}?', { name: user?.name })}</h3>
            <p className="text-sm text-on-surface-variant mb-5">{kickConfirm.communityName}</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setKickConfirm(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-on-surface-variant hover:bg-surface-container transition-colors">{t('Cancelar')}</button>
              <button
                onClick={async () => { await kickFromCommunity(kickConfirm.communityId, targetId); setKickConfirm(null); }}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-error text-on-error hover:opacity-90 transition-opacity"
              >
                {t('Kick from community')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ban modal */}
      {banModal && (
        <BanModalProfile
          communityName={banModal.communityName}
          userName={user?.name}
          onConfirm={async (reason) => { await banFromCommunity(banModal.communityId, targetId, reason); setBanModal(null); }}
          onCancel={() => setBanModal(null)}
        />
      )}

      {/* Toast from moderation hook */}
      {moderationToast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg text-sm font-medium ${
          moderationToast.type === 'error' ? 'bg-error-container text-on-error-container' : 'bg-on-surface text-surface'
        }`}>
          {moderationToast.message}
        </div>
      )}
    </div>
  );
}

function BanModalProfile({ communityName, userName, onConfirm, onCancel }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-surface-container-lowest rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
        <h3 className="text-base font-bold text-on-surface mb-1">{t('Are you sure you want to ban {{name}}?', { name: userName })}</h3>
        <p className="text-sm text-on-surface-variant mb-3">{communityName}</p>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-outline-variant/30 bg-transparent text-on-surface text-sm resize-none mb-4 placeholder:text-on-surface-variant/50"
          placeholder={t('Reason (optional)')}
        />
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium text-on-surface-variant hover:bg-surface-container transition-colors">{t('Cancelar')}</button>
          <button
            onClick={() => onConfirm(reason.trim() || null)}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-error text-on-error hover:opacity-90 transition-opacity"
          >
            {t('Ban from community')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserProfileNew;
